import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import './resize-line.scss';

interface ResizeLineProps {
  target: RefObject<HTMLDivElement>;
  max: number;
  min: number;
  additionStyles?: { [key: string]: string };
  handleResize: (value: number) => void;
  additionProps?: { [prop: string]: unknown };
  addClassNames?: string;
  side: 'top' | 'bottom' | 'left' | 'right';
  showIcon?: boolean;
  mouseDown?: () => void;
  mouseUp?: () => void;
}

export function ResizeLine(props: ResizeLineProps) {
  const { target, max, min, additionStyles, handleResize, additionProps = {}, addClassNames = '', side } = props;
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isResizeMouseDown, setIsResizeMouseDown] = useState(false);
  const cursorClass = useMemo(() => {
    return side === 'top' || side === 'bottom' ? 'webnotes-cursor-ns-resize' : 'webnotes-cursor-ew-resize';
  }, [side]);

  const ref = useRef<HTMLDivElement | null>(null);

  const handleResizerMouseDown = useCallback(() => {
    setIsMouseDown(true);
    document.body.classList.add(cursorClass);
  }, [cursorClass]);

  const resizeMouseDown = useCallback(() => {
    if (!isResizeMouseDown) {
      setIsResizeMouseDown(true);
      props.mouseDown?.();
    }
  }, [isResizeMouseDown, props]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isMouseDown) {
        const rect = target.current?.getBoundingClientRect();
        if (rect) {
          if (side === 'right' || side === 'left') {
            let width = side === 'right' ? e.pageX - rect.left : rect.right - e.pageX;
            if (width <= min) width = min;
            if (width >= max) width = max;
            handleResize(width);
          } else if (side === 'top' || side === 'bottom') {
            let height = side === 'top' ? rect.bottom - e.pageY : e.pageY - rect.top;
            if (height <= min) height = min;
            if (height >= max) height = max;
            handleResize(height);
          }
        }
      }
    };

    const handleMouseUp = () => {
      if (isMouseDown) {
        setIsResizeMouseDown(false);
        props.mouseUp?.();
        setIsMouseDown(false);
        document.body.classList.remove(cursorClass);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMouseDown, target, handleResize, side, min, max, cursorClass, props.mouseUp, props.mouseDown, props]);

  useEffect(() => {
    const element = ref?.current;
    element?.addEventListener('mousedown', resizeMouseDown);

    return () => {
      element?.removeEventListener('mousedown', resizeMouseDown);
    };
  }, [resizeMouseDown]);

  return (
    <div
      ref={ref}
      className={classNames(
        'absolute bg-transparent select-none',
        {
          'right-[-5px]': side === 'right',
          'left-[-5px]': side === 'left',
          'top-0 h-full w-[10px] cursor-ew-resize': side === 'left' || side === 'right',
          'top-[-5px]': side === 'top',
          'bottom-[-5px]': side === 'bottom',
          'left-0 h-[10px] w-full cursor-ns-resize': side === 'top' || side === 'bottom',
        },
        addClassNames
      )}
      style={additionStyles ?? {}}
      onMouseDown={handleResizerMouseDown}
      {...additionProps}
    >
      {props.showIcon && (
        <div className="absolute w-5 h-5 bg-white rounded border border-x-slate-100 top-24 -left-2.5 flex items-center justify-center">
          <Icon icon={IconsMap.ARROW_DOUBLE_ARROV_HORIZONTAL} className="w-4 h-4" />
        </div>
      )}
    </div>
  );
}
